import {
  fieldValuesToStartingPosition,
  calcTotalSpendFromFieldValues,
  findHighestIncrementalRevenueChannel,
  findLowestIncrementalRevenueChannel,
  startingFieldValuesFromCurrent,
  addCalculatedRevenue,
} from './optimizeHelpers';
import { getRevenueFromResponseCurve, getCurrentResponseCurve } from '../../MarketingMixModeling/forecast/forecastData';
import { OPTIMIZATION_OPTIONS } from '../constants';

export const INCREMENT = 50;
const errorMessage = x => `The ${x} target you've entered exceeds the achievable range based on the model's current estimates. Please adjust your target.`;

/**
 * Optimizes the field values to meet the target spend by incrementing the recommended values of the highest ROI channel.
 * Optimization starts at the *minimum value* for each channel and budget is allocated to the highest ROI channel until:
 * - the target spend is met or
 * - all channel level constraints are met or
 * - the optional ROAS target is no longer met
 *
 * @param {number} targetSpend - The target spend to optimize towards.
 * @param {object} constraints - The constraints for each channel.
 * @param {object} fieldValues - The current field values.
 * @param {number} baseline - The baseline revenue.
 * @param {object} responseCurves - The response curves for each channel.
 * @param {number} [optionalTarget] - Optional ROAS target that must be maintained during optimization.
 * @returns {object} - The optimized field values.
 */
export const optimizeToBudget = (targetSpend, constraints, fieldValues, baseline, responseCurves, optionalTarget) => {
  const startingFieldValues = fieldValuesToStartingPosition(fieldValues, constraints, 'min_value');
  const newFieldValues = addCalculatedRevenue(startingFieldValues, responseCurves);
  const startingSpend = calcTotalSpendFromFieldValues(newFieldValues);
  const startingRevenue = calcTotalSpendFromFieldValues(newFieldValues, 'calculatedRevenue');
  let metrics = {
    spend: startingSpend,
    revenue: baseline + startingRevenue,
    roas: (baseline + startingRevenue) / startingSpend,
  };
  if (optionalTarget && metrics.roas < optionalTarget) {
    throw new Error(errorMessage('ROAS [CPA]'));
  }

  while (metrics.spend < targetSpend && (!optionalTarget || metrics.roas >= optionalTarget)) {
    try {
      const highestIncrementalRevenueChannel = findHighestIncrementalRevenueChannel(newFieldValues, constraints, responseCurves);
      const channelData = newFieldValues[highestIncrementalRevenueChannel];

      // Update the channel spend data
      const updatedSpend = Math.min(channelData.recommended + INCREMENT, constraints[highestIncrementalRevenueChannel].max_value);
      channelData.recommended = updatedSpend;
      channelData.value = channelData.recommended;

      // Determine the revenue for the channel
      const responseCurveForChannel = getCurrentResponseCurve(responseCurves, highestIncrementalRevenueChannel);
      const calculatedRevenue = getRevenueFromResponseCurve(responseCurveForChannel.values, channelData.value);
      channelData.calculatedRevenue = calculatedRevenue;

      // Update the total spend and revenue and calculate the overall ROAS
      metrics = {
        spend: metrics.spend + INCREMENT,
        revenue: baseline + calcTotalSpendFromFieldValues(newFieldValues, 'calculatedRevenue'),
      };
      metrics.roas = metrics.revenue / metrics.spend;

      // Check if initial ROAS exceeds optional target
      if (metrics.spend === startingSpend + INCREMENT && optionalTarget && metrics.roas < optionalTarget) {
        throw new Error(errorMessage('ROAS [CPA]'));
      }
    } catch (e) {
      throw new Error(e.message);
    }
  }
  return newFieldValues;
};
/**
 * Optimizes the budget by expanding the spend on the highest ROI channel until the target spend is reached.
 * Optimization starts at the current value for each channel and budget is allocated to the highest ROI channel until:
 * - the target spend is met or
 * - all channel level constraints are met or
 * - the optional ROAS target is no longer met
 *
 * @param {number} targetSpend - The target spend to reach.
 * @param {object} constraints - The constraints for each channel.
 * @param {object} fieldValues - The current field values.
 * @param {number} baseline - The baseline revenue.
 * @param {object} responseCurves - The response curves for each channel.
 * @param {number} [optionalTarget] - Optional ROAS target that must be maintained during optimization.
 * @returns {object} - The new field values after optimization.
 */
export const optimizeExpandBudget = (targetSpend, constraints, fieldValues, baseline, responseCurves, optionalTarget) => {
  const startingFieldValues = startingFieldValuesFromCurrent(fieldValues, constraints);
  const newFieldValues = addCalculatedRevenue(startingFieldValues, responseCurves);
  const startingSpend = calcTotalSpendFromFieldValues(newFieldValues);
  const startingRevenue = calcTotalSpendFromFieldValues(newFieldValues, 'calculatedRevenue');
  let metrics = {
    spend: startingSpend,
    revenue: baseline + startingRevenue,
    roas: (baseline + startingRevenue) / startingSpend,
  };

  while (metrics.spend < targetSpend && (!optionalTarget || metrics.roas >= optionalTarget)) {
    try {
      const highestIncrementalRevenueChannel = findHighestIncrementalRevenueChannel(newFieldValues, constraints, responseCurves);
      const channelData = newFieldValues[highestIncrementalRevenueChannel];

      // Update the channel spend data
      const updatedSpend = Math.min(channelData.recommended + INCREMENT, constraints[highestIncrementalRevenueChannel].max_value);
      channelData.recommended = updatedSpend;
      channelData.value = channelData.recommended;

      // Determine the revenue for the channel
      const responseCurveForChannel = getCurrentResponseCurve(responseCurves, highestIncrementalRevenueChannel);
      const calculatedRevenue = getRevenueFromResponseCurve(responseCurveForChannel.values, channelData.value);
      channelData.calculatedRevenue = calculatedRevenue;

      // Update the total spend and revenue and calculate the overall ROAS
      metrics = {
        spend: metrics.spend + INCREMENT,
        revenue: baseline + calcTotalSpendFromFieldValues(newFieldValues, 'calculatedRevenue'),
      };
      metrics.roas = metrics.revenue / metrics.spend;
    } catch (e) {
      console.log(e);
      break;
    }
  }
  return newFieldValues;
};

/**
 * Optimizes the budget by reducing spend on channels with the lowest ROI until the target spend is reached.
 * Optimization starts at the *current value* for each channel and budget is reduced from the lowest ROI channel until:
 * - the target spend is met or
 * - all channel level constraints are met or
 * - the optional ROAS target is no longer met
 *
 * The optimization process:
 * 1. Starts with current spend levels
 * 2. Identifies channel with lowest incremental ROI
 * 3. Reduces spend on that channel by fixed increment
 * 4. Recalculates overall metrics
 * 5. Repeats until:
 *    - Target spend is reached, or
 *    - Channel constraints are hit, or
 *    - Optional ROAS target is no longer maintained
 *
 * @param {number} targetSpend - The target spend amount to reduce to
 * @param {object} constraints - The min/max constraints for each channel
 * @param {object} fieldValues - The current field values with spend amounts
 * @param {number} baseline - The baseline revenue amount
 * @param {object} responseCurves - The response curves mapping spend to revenue
 * @param {number} [optionalTarget] - Optional ROAS target that must be maintained
 * @returns {object} - The optimized field values with updated spend amounts
 */
export const optimizeReduceBudget = (targetSpend, constraints, fieldValues, baseline, responseCurves, optionalTarget) => {
  const startingFieldValues = startingFieldValuesFromCurrent(fieldValues, constraints);
  const newFieldValues = addCalculatedRevenue(startingFieldValues, responseCurves);
  const startingSpend = calcTotalSpendFromFieldValues(newFieldValues);
  const startingRevenue = calcTotalSpendFromFieldValues(newFieldValues, 'calculatedRevenue');
  let metrics = {
    spend: startingSpend,
    revenue: baseline + startingRevenue,
    roas: (baseline + startingRevenue) / startingSpend,
  };

  while (metrics.spend > targetSpend && (!optionalTarget || metrics.roas <= optionalTarget)) {
    try {
      const lowestIncrementalRevenueChannel = findLowestIncrementalRevenueChannel(newFieldValues, constraints, responseCurves);
      const channelData = newFieldValues[lowestIncrementalRevenueChannel];

      // Update the channel spend data
      const updatedSpend = Math.max(channelData.recommended - INCREMENT, constraints[lowestIncrementalRevenueChannel].min_value);
      channelData.recommended = updatedSpend;
      channelData.value = channelData.recommended;

      // Determine the revenue for the channel
      const responseCurveForChannel = getCurrentResponseCurve(responseCurves, lowestIncrementalRevenueChannel);
      const calculatedRevenue = getRevenueFromResponseCurve(responseCurveForChannel.values, channelData.value);
      channelData.calculatedRevenue = calculatedRevenue;

      // Update the total spend and revenue and calculate the overall ROAS
      metrics = {
        spend: metrics.spend - INCREMENT,
        revenue: baseline + calcTotalSpendFromFieldValues(newFieldValues, 'calculatedRevenue'),
      };
      metrics.roas = metrics.revenue / metrics.spend;
    } catch (e) {
      console.log(e);
      break;
    }
  }
  return newFieldValues;
};

/**
 * Optimizes the Return on Advertising Spend (ROAS) by adjusting spend values for different channels.
 * Optimization starts at the *minimum value* for each channel and incrementally increases spend on
 * channels with the highest ROI.
 *
 * The optimization continues until either:
 * - The overall ROAS drops below the target ROAS
 * - The optional budget limit is reached
 * - All channels reach their maximum spend constraints
 *
 * @param {number} targetROAS - The target ROAS value to optimize towards
 * @param {object} constraints - The min/max spend constraints for each channel
 * @param {object} fieldValues - The current spend values for each channel
 * @param {number} baseline - The baseline revenue value
 * @param {object} responseCurves - The response curves defining revenue vs spend for each channel
 * @param {number} [optionalBudget] - Optional budget limit that cannot be exceeded
 * @returns {object} - The optimized spend values for each channel
 * @throws {Error} - If the target budget cannot be achieved within constraints
 */
export const optimizeROAS = (targetROAS, constraints, fieldValues, baseline, responseCurves, optionalBudget) => {
  const startingFieldValues = fieldValuesToStartingPosition(fieldValues, constraints, 'min_value');
  const newFieldValues = addCalculatedRevenue(startingFieldValues, responseCurves);
  const startingSpend = calcTotalSpendFromFieldValues(newFieldValues);
  const startingRevenue = calcTotalSpendFromFieldValues(newFieldValues, 'calculatedRevenue');
  let metrics = {
    spend: startingSpend,
    revenue: baseline + startingRevenue,
    roas: (baseline + startingRevenue) / startingSpend,
  };
  if (optionalBudget && metrics.spend > optionalBudget) {
    throw new Error(errorMessage('spend'));
  }

  while (metrics.roas >= targetROAS && (!optionalBudget || metrics.spend <= optionalBudget)) {
    try {
      const highestIncrementalRevenueChannel = findHighestIncrementalRevenueChannel(newFieldValues, constraints, responseCurves);
      const channelData = newFieldValues[highestIncrementalRevenueChannel];

      // Update the channel spend data
      const updatedSpend = Math.min(channelData.recommended + INCREMENT, constraints[highestIncrementalRevenueChannel].max_value);
      channelData.recommended = updatedSpend;
      channelData.value = channelData.recommended;

      // Determine the revenue for the channel
      const responseCurveForChannel = getCurrentResponseCurve(responseCurves, highestIncrementalRevenueChannel);
      const calculatedRevenue = getRevenueFromResponseCurve(responseCurveForChannel.values, channelData.value);
      channelData.calculatedRevenue = calculatedRevenue;

      // Update the total spend and revenue and calculate the overall ROAS
      metrics = {
        spend: metrics.spend + INCREMENT,
        revenue: baseline + calcTotalSpendFromFieldValues(newFieldValues, 'calculatedRevenue'),
      };
      metrics.roas = metrics.revenue / metrics.spend;

      // Check if initial ROAS exceeds optional target
      if (metrics.spend === startingSpend + INCREMENT && optionalBudget && metrics.spend > optionalBudget) {
        throw new Error(errorMessage('spend'));
      }
    } catch (e) {
      throw new Error(e.message);
    }
  }
  return newFieldValues;
};

/**
 * Calculates the target spend based on the given optimization type, constraints, budget, and KPI type.
 *
 * @param {string} optimization - The type of optimization to be performed (e.g. 'max_kpi', etc.).
 * @param {Object} constraints - The constraints for the optimization, each having 'max_value' and 'min_value'.
 * @param {number} budget - The budget value for optimization.
 * @param {boolean} isConversionKPI - Indicates if the KPI is a conversion KPI.
 * @returns {number} The calculated target spend.
 */

const getTargetSpend = (optimization, constraints, budget) => {
  const totalConstraints = Object.values(constraints).reduce((p, v) => ({
    max: p.max + v.max_value,
    min: p.min + v.min_value,
  }), { max: 0, min: 0 });

  switch (optimization) {
    case OPTIMIZATION_OPTIONS.maxKPIs:
      return parseInt(budget) || totalConstraints.max;
    case OPTIMIZATION_OPTIONS.expandBudget:
      return parseInt(budget);
    case OPTIMIZATION_OPTIONS.reduceBudget:
      return parseInt(budget);
    case OPTIMIZATION_OPTIONS.targetMetric:
      return Number(budget);
    default:
  }
};

/**
 * Retrieves the appropriate optimization function based on the given optimization type.
 *
 * @param {string} optimization - The type of optimization to be performed (e.g., 'max_kpi', etc.).
 * @returns {Function|null} The corresponding optimization function, or null if no match is found.
 */
const getOptimizationFunction = optimization => {
  switch (optimization) {
    case OPTIMIZATION_OPTIONS.maxKPIs:
      return optimizeToBudget;
    case OPTIMIZATION_OPTIONS.expandBudget:
      return optimizeExpandBudget;
    case OPTIMIZATION_OPTIONS.reduceBudget:
      return optimizeReduceBudget;
    case OPTIMIZATION_OPTIONS.targetMetric:
      return optimizeROAS;
    default:
      return null;
  }
};

/**
 * Calculates the optimized values based on the given parameters.
 *
 * @param {string} optimization - The type of optimization to be performed (e.g., 'max_kpi', etc.).
 * @param {number} budget - The budget value for optimization.
 * @param {Object} constraints - The constraints for the optimization, each having 'max_value' and 'min_value'.
 * @param {Object} featureRecommendationsObj - The feature recommendations object containing baseline spend values.
 * @param {number} baseline - The baseline revenue/conversions value.
 * @param {Object[]} responseCurves - Array of response curve objects for each channel.
 * @param {number} [optionalTarget] - Optional target value for optimization (e.g. target ROAS).
 * @returns {Object} The calculated optimization values mapping channels to spend amounts.
 */
export const calcOptimization = (optimization, budget, constraints, featureRecommendationsObj, baseline, responseCurves, optionalTarget) => {
  const targetSpend = getTargetSpend(optimization, constraints, budget);
  const optimizationFunction = getOptimizationFunction(optimization);
  const optionalTargetMetric = optionalTarget ? Number(optionalTarget) : optionalTarget;
  return optimizationFunction(targetSpend, constraints, featureRecommendationsObj, baseline, responseCurves, optionalTargetMetric);
};
